<script setup lang="ts">
import useCurrentUserFavoriteComposable from '@src/favorite/composable/current-user-favorite-composable.ts';
import DragHandleIcon from '@src/application/drag-handle-icon.vue';
import { VueDraggableNext } from 'vue-draggable-next';
import { ref } from 'vue';
import { FavoriteResponse } from '@src/api/interface/response/favorite-response.ts';
import CenterChildrenWrapper from '@components/layout-wrapper/center-children-wrapper.vue';
import Button from '@components/buttons/button.vue';
import Input from '@components/form/input.vue';
import { EditFavoriteRequest } from '@src/api/interface/request/edit-favorite-request.ts';
import Text from '@components/text.vue';
import HorizontalAlignChildrenWrapper from '@components/layout-wrapper/horizontal-align-children-wrapper.vue';
import Card from '@components/card.vue';
import ButtonSquare from '@components/buttons/button-square.vue';
import Separator from '@components/separator.vue';

const emits = defineEmits<((event: 'saved') => void)>();

const { favorites, editFavorites, removeFavorites } = useCurrentUserFavoriteComposable();

type FavoriteToEdit = FavoriteResponse & { newTitle?: string|null };

const favoritesToEdit = ref<Array<FavoriteToEdit>>(favorites.value ? [...favorites.value] : []);
const favoritesToRemove = ref<Array<FavoriteToEdit>>([]);

async function editFavoriteEntries() {
    const requestsToEdit: Array<EditFavoriteRequest> = [];
    for (const favoriteToEdit of favoritesToEdit.value) {
        requestsToEdit.push({
            id: favoriteToEdit.id,
            title: favoriteToEdit.newTitle ?? favoriteToEdit.title,
        });
    }

    await editFavorites(requestsToEdit);
    await removeFavorites(favoritesToRemove.value);

    emits('saved');
}

function remove(favorite: FavoriteToEdit) {
    favoritesToRemove.value.push(favorite);
    const index = favoritesToEdit.value!.indexOf(favorite);
    favoritesToEdit.value!.splice(index, 1);
}
</script>

<template>
    <div>
        <VueDraggableNext v-if="favoritesToEdit.length > 0" :list="favoritesToEdit">
            <HorizontalAlignChildrenWrapper v-for="favoriteToEdit in favoritesToEdit" :key="favoriteToEdit.id" :class="$style.favorite" :wrap-on-small-screen="false" grow-children>
                <Card card-color="white">
                    <DragHandleIcon/>
                    <HorizontalAlignChildrenWrapper spread-self center-children gap="1" gap-on-small-screen="0">
                        <Text :class="$style.title">{{ favoriteToEdit.title }}</Text>
                        <Input placeholder="Neuer Titel" disable-input-growing-by-hidden-placeholder @update:model-value="favoriteToEdit.newTitle = $event"/>
                    </HorizontalAlignChildrenWrapper>
                </Card>
                <ButtonSquare icon="trash-alt" icon-color="red" context="danger" @click="remove(favoriteToEdit)"/>
            </HorizontalAlignChildrenWrapper>
        </VueDraggableNext>
        <template v-else>
            <Card card-color="blue" card-icon="info-circle">Du hast alle Favoriten entfernt. Speichere, um deine Änderungen zu übernehmen.</Card>
        </template>
        <Separator gap="1" :border="false"/>
        <CenterChildrenWrapper>
            <Button context="primary" @click="editFavoriteEntries">Speichern</Button>
        </CenterChildrenWrapper>
    </div>
</template>

<style lang="scss" module>
.favorite {
    margin-bottom: .5em;
    cursor: pointer;
}
.title {
    display: inline-flex;
    align-items: center;
    padding: .5em 0;
    font-weight: bold;

    @media(max-width: 33em) {
        text-align: center;
    }

    @media(min-width: 40em) {
        width: 15em;
    }
}
</style>
